/**
 * Compute the full set of dynamic-route params SvelteKit should prerender.
 *
 * Why this exists (issue #63):
 * SvelteKit 2.x's prerender crawler is non-deterministic for routes whose
 * URLs are produced at render time (here: `getMatchPath` / `getTeamPath`,
 * which slugify contestant names off the live data). Two consecutive
 * `pnpm build`s can crawl a different subset of `/matches/[slugpath]/**`
 * and `/teams/[slug]/**`, producing builds that disagree on which HTML
 * files exist — even when the JS bundles are byte-identical (the #63
 * fix in `bin/build-version/index.js`).
 *
 * The fix: each dynamic route's `+page.js` exports `entries()` returning
 * the param list computed here. SvelteKit guarantees every returned entry
 * is prerendered, so the output set stops depending on crawler luck and
 * matches the data byte-for-byte.
 *
 * Source of truth is `src/statics/data/current/matches.json` — the same
 * file the runtime data layer reads (`src/utils/data/urls.js`). It's
 * committed to the repo and refreshed by the data toolkit, so building
 * against it keeps prerendered URLs in sync with whatever the app
 * actually fetches on load. The fallback bundle is empty by design, so
 * an empty prerender list there is also correct.
 *
 * If a match slug genuinely doesn't exist yet (e.g. an unresolved
 * knockout where both contestants are TBD), `buildMatchPath` returns
 * `'.'`, which we filter out — there's no real page to build.
 */

import { buildMatchPath } from '$utils/data/decorateMatches';
import currentMatches from '../../statics/data/current/matches.json';
import parseData from '$utils/data/parseData';
import slugify from '$utils/slugify';

/**
 * @typedef {{ matchInfo?: { contestant?: Array<{ id?: string, name?: string }> } }} Match
 */

/**
 * Read matches from the bundled `current/matches.json`, then run them
 * through the same `parseData` pipeline the runtime uses (latinize +
 * locale rephrases like `Cabo Verde` → `Cape Verde`). Slugs computed
 * here have to match the post-rename slugs used by the data layer,
 * otherwise the dynamic route's `+layout.js` 404s on a slug the prerender
 * step generated — see the warning in `decorateMatches.js`.
 *
 * @returns {Match[]}
 */
function readMatches() {
  const list = currentMatches?.matches?.match;
  if (!Array.isArray(list)) return [];
  // `parseData` operates on the full payload shape; pass the whole
  // object through and pull `matches.match` back out.
  const parsed = parseData({ matches: { match: list } });
  const out = parsed?.matches?.match;
  return Array.isArray(out) ? out : [];
}

/**
 * `<home>-<away>-MM-DD` slugpaths for the `/matches/[slugpath]` route.
 * Strips the `matches/` prefix and trailing slash off the value
 * `buildMatchPath` returns so it matches `params.slugpath`.
 *
 * @returns {string[]}
 */
export function getMatchSlugpaths() {
  const slugpaths = new Set();
  for (const match of readMatches()) {
    const { path } = buildMatchPath(match);
    if (!path || path === '.') continue;
    // path is `matches/<slugpath>/` — pull out just `<slugpath>`.
    const m = path.match(/^matches\/(.+?)\/?$/);
    if (m) slugpaths.add(m[1]);
  }
  return [...slugpaths].sort();
}

/**
 * Team slugs for the `/teams/[slug]` route. Matches the slug logic in
 * `src/utils/getTeams/index.js` (slugify on contestant name, skip
 * placeholder TBD contestants).
 *
 * @returns {string[]}
 */
export function getTeamSlugs() {
  const slugs = new Set();
  for (const match of readMatches()) {
    const contestants = match?.matchInfo?.contestant;
    if (!Array.isArray(contestants) || contestants.length < 2) continue;
    if (!contestants[0]?.name || !contestants[1]?.name) continue;
    for (const c of contestants) {
      if (c?.name) slugs.add(slugify(c.name));
    }
  }
  return [...slugs].sort();
}
